import { useMsal } from '@azure/msal-react';
import {
  ArrowBackRounded,
  VisibilityOffRounded,
  VisibilityRounded
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Collapse,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Paper,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { GetForgotPasswordParams } from '../../api/reset-password-external-users';
import SCGlogo from '../../assets/logoSCG_none.png';
import GradientText from '../../components/GradientText';
import { useAppDispatch } from '../../hook/useAppRudux';
import { redirectAfterLogin, signInSCGRedirect } from '../Auth/authSCG';
import loadingSlice from '../Loading/loadingSlice';
import { getForgotPasswordAsync } from '../ResetPassword/resetPasswordSlice';
import { getExternalUserAsync } from '../UserProfile/userProfileSlice';

interface AccountState {
  email: string;
  password: string;
  showPassword: boolean;
}

export default function Login() {
  const { instance } = useMsal();
  const dispatch = useAppDispatch();
  const history = useHistory();

  const [account, setAccount] = useState<AccountState>({
    email: '',
    password: '',
    showPassword: false
  });
  const [openLoginEmail, setOpenLoginEmail] = useState<boolean>(false);
  const [openForgotPassword, setOpenForgotPassword] = useState<boolean>(false);

  const disabled = !(
    Boolean(account.email) &&
    (Boolean(account.password) || openForgotPassword)
  );

  function handleClickShowPassword() {
    setAccount({ ...account, showPassword: !account.showPassword });
  }

  function handleMouseDownPassword(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      if (openForgotPassword) {
        const forgotPassword: GetForgotPasswordParams = {
          email: account.email
        };
        await dispatch(getForgotPasswordAsync(forgotPassword)).unwrap();
        dispatch(
          loadingSlice.actions.success({
            message: 'Password reset email was sent, please check your inbox'
          })
        );
      } else {
        await dispatch(getExternalUserAsync(account)).unwrap();
        redirectAfterLogin(history);
        dispatch(loadingSlice.actions.clear());
      }
    } catch {
      setAccount({ ...account, password: '' });
    }
  }

  return (
    <Container
      component="main"
      maxWidth="md"
      sx={{ mt: { xs: 8, sm: 0 }, mb: 16 }}
    >
      <Grid container>
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              mt: { xs: 6, sm: 24 },
              mr: { xs: 0, sm: 4 },
              textAlign: { xs: 'center', sm: 'left' }
            }}
          >
            <Typography
              sx={{
                ml: { sm: -0.25 },
                fontFamily: 'Comfortaa',
                fontSize: { xs: '3rem', md: '3.5rem' },
                fontWeight: 700,
                letterSpacing: -2
              }}
              variant="h5"
            >
              <GradientText>backspace</GradientText>
            </Typography>
            <Typography
              sx={{
                mt: 1,
                fontSize: { xs: '1rem', sm: '1.2rem', md: '1.5rem' }
              }}
              color="textPrimary"
              variant="h5"
            >
              We provide one stop service for Engineering and Construction
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Paper
            sx={{
              px: 2,
              py: 1,
              mt: { xs: 4, sm: 16 },
              mb: 4,
              maxWidth: { xs: '100%', sm: 400 },
              borderRadius: '16px'
            }}
          >
            <Collapse in={!openLoginEmail}>
              <Typography
                sx={{ textAlign: 'center', mt: 2, mb: 2.5 }}
                color="textPrimary"
                variant="h5"
              >
                Log in
              </Typography>
              <Typography color="textSecondary" variant="subtitle1">
                for SCG user
              </Typography>
              <Button
                sx={{
                  fontSize: 16,
                  p: '0.5rem 1rem',
                  width: '100%',
                  color: '#ffffff',
                  bgcolor: '#ED1B24',
                  '&:hover, &.Mui-focusVisible': {
                    bgcolor: '#D41721'
                  }
                }}
                variant="contained"
                size="large"
                fullWidth
                startIcon={
                  <Avatar sx={{ width: 28, height: 28 }} src={SCGlogo}></Avatar>
                }
                onClick={() => signInSCGRedirect(instance)}
              >
                Continue with SCG
              </Button>
              <Box
                sx={{ mt: 1.2, mb: 0.25 }}
                display="flex"
                alignItems="center"
              >
                <Divider sx={{ flexGrow: 1 }} />
                <Typography sx={{ mx: 1 }} variant="caption">
                  OR
                </Typography>
                <Divider sx={{ flexGrow: 1 }} />
              </Box>
              <Typography color="textSecondary" variant="subtitle1">
                for External user
              </Typography>
              <Button
                sx={{
                  mb: 1,
                  fontSize: 16,
                  p: '0.5rem 1rem'
                }}
                color="primary"
                variant="contained"
                size="large"
                fullWidth
                onClick={() => setOpenLoginEmail(true)}
              >
                Continue with email
              </Button>
              <Box display="flex" justifyContent="center" alignItems="center">
                <Button
                  sx={{
                    textTransform: 'none',
                    textDecoration: 'underline',
                    mb: 1,

                    '&:hover, &.Mui-focusVisible': {
                      color: (theme) => theme.palette.primary.main,
                      bgcolor: 'transparent'
                    }
                  }}
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setOpenLoginEmail(true);
                    setOpenForgotPassword(true);
                  }}
                  disableRipple
                >
                  Forgot password?
                </Button>
              </Box>
            </Collapse>
            <Collapse in={openLoginEmail}>
              <Box sx={{ my: 1, display: 'flex', alignItems: 'center' }}>
                <Tooltip title="Back">
                  <IconButton
                    aria-label="back"
                    edge="start"
                    size="large"
                    onClick={() => {
                      setOpenLoginEmail(false);
                      setTimeout(() => {
                        setOpenForgotPassword(false);
                      }, 200);
                    }}
                  >
                    <ArrowBackRounded />
                  </IconButton>
                </Tooltip>
                <Typography color="textPrimary" variant="h6">
                  {openForgotPassword
                    ? 'Reset password'
                    : 'Log in for External user'}
                </Typography>
              </Box>
              {openForgotPassword && (
                <Typography
                  sx={{ fontSize: 12, mb: 0.5 }}
                  color="text.secondary"
                  variant="body2"
                >
                  Enter your email address and we will send you a password reset
                  link
                </Typography>
              )}
              <form onSubmit={onSubmit}>
                <TextField
                  sx={{ m: '0.5rem 0', width: '100%' }}
                  id="email"
                  label="Email"
                  placeholder="Enter your email address..."
                  type="email"
                  variant="outlined"
                  size="small"
                  value={account.email}
                  onChange={(event) =>
                    setAccount({ ...account, email: event.target.value })
                  }
                />
                {!openForgotPassword && (
                  <FormControl
                    sx={{ m: '0.5rem 0', width: '100%' }}
                    variant="outlined"
                    size="small"
                  >
                    <InputLabel htmlFor="password">Password</InputLabel>
                    <OutlinedInput
                      id="password"
                      type={account.showPassword ? 'text' : 'password'}
                      value={account.password}
                      onChange={(event) =>
                        setAccount({ ...account, password: event.target.value })
                      }
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                            size="large"
                          >
                            {account.showPassword ? (
                              <Tooltip title="Hide password">
                                <VisibilityOffRounded />
                              </Tooltip>
                            ) : (
                              <Tooltip title="Show password">
                                <VisibilityRounded />
                              </Tooltip>
                            )}
                          </IconButton>
                        </InputAdornment>
                      }
                      label="Password"
                      placeholder="Enter your password..."
                    />
                  </FormControl>
                )}
                <Button
                  sx={{ mt: 3, mb: 1, fontSize: 16 }}
                  type="submit"
                  color="primary"
                  variant="contained"
                  size="large"
                  disabled={disabled}
                  fullWidth
                >
                  {openForgotPassword ? 'Send reset link' : 'Log in'}
                </Button>
                <Box display="flex" justifyContent="center" alignItems="center">
                  <Button
                    sx={{
                      textTransform: 'none',
                      textDecoration: 'underline',
                      mb: 1,

                      '&:hover, &.Mui-focusVisible': {
                        color: (theme) => theme.palette.primary.main,
                        bgcolor: 'transparent'
                      }
                    }}
                    color="inherit"
                    size="small"
                    onClick={() => setOpenForgotPassword(!openForgotPassword)}
                    disableRipple
                  >
                    {openForgotPassword
                      ? 'Continue with email'
                      : 'Forgot password?'}
                  </Button>
                </Box>
              </form>
            </Collapse>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
